import { createContext, useContext } from 'react';

/**
 * How many times the current route has been asked to load its chunk again.
 *
 * `React.lazy` **caches** the promise it was given: once an import is rejected, the lazy object
 * holds that rejection forever and every later render re-throws it — so a «تلاش مجدد» button that
 * only clears the boundary's state cannot recover from a failed chunk (the plan's ruling E, the
 * cached-rejection note). The way out is a *new* lazy object for the next attempt, and the only
 * honest way to ask for one is to tell the page which attempt it is on.
 *
 * The number comes from the `ErrorBoundary` in the shell (it owns the retry button); the page
 * wrapper (`app/lazyPage.tsx`) reads it and keys its lazy object by it. Attempt 0 is the ordinary
 * lazy object — created once per route and reused for the life of the tab, so navigating back to a
 * page that is already loaded does **not** suspend again. Attempt 1 and beyond are fresh objects,
 * each one a fresh `import()` and therefore a fresh chance for the chunk to arrive.
 */
export const RouteAttemptContext = createContext(0);

/** The attempt number of the route being rendered right now. */
export function useRouteAttempt(): number {
  return useContext(RouteAttemptContext);
}
